/**
 * DropoffSection — how the wig gets IN, as two top-level options
 * (Shmuel 2026-07-03):
 *
 *   1. I'll drop it off myself → pick the drop-off date
 *   2. Send it in with Uber   → now, or scheduled for a date + time
 *
 * Lifted state: parent owns dropDate + the Uber flags so the booking
 * submit handler can ship them to /api/book. State contract:
 * uberDropoff=false means self drop-off; uberSchedule only matters
 * when uberDropoff is true.
 *
 * The date `<input type="date">` uses the wrapped-label trick from
 * bug #125: native iOS Safari renders an empty date input as a
 * near-invisible gray pill with no placeholder. We layer a visible
 * label that shows "Choose a date" until a value is set, with the
 * native input absolutely positioned over it at opacity 0 so the
 * picker still fires on tap. Same pattern as /book's appointment
 * date input.
 *
 * Extracted from /book/page.tsx — Phase 1.7 of booking consolidation
 * (wiki/plans/booking-consolidation-2026-04-28.md).
 */
"use client";

import { Calendar, Package, Clock, Truck } from "lucide-react";
import { useTranslations } from "next-intl";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { formatPrice } from "@/lib/utils";

/** "14:30" → "2:30 PM" for the visible time-picker label. */
function formatTimeLabel(t: string): string {
  const [h, m] = t.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return t;
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

interface DropoffSectionProps {
  /** ISO date "YYYY-MM-DD" (lifted from parent). */
  dropDate: string;
  setDropDate: (d: string) => void;
  /** Send it IN with Uber (courier collects from the customer). */
  uberDropoff: boolean;
  setUberDropoff: (v: boolean) => void;
  /** Send it BACK with Uber once the work is done. */
  uberReturn: boolean;
  setUberReturn: (v: boolean) => void;
  /** Customer wants to come in for styling once the wash & set is done. */
  wantsStyling: boolean;
  setWantsStyling: (v: boolean) => void;
  /** How many drop-off wigs the customer has. Drives "wig" vs "wigs" copy. */
  dropoffWigCount: number;
  /**
   * Service-id → required service-turnaround days. Comes from
   * AppointmentSettings.dropoffDaysByType. Used to compute how long
   * the customer's wig will be in the salon and surface that as a
   * lead-time callout up top.
   */
  dropoffDaysByType?: Record<string, number>;
  /** Service ids selected across all drop-off wigs. */
  selectedDropoffServiceIds?: string[];
  /** Uber timing — false = dispatch now (default), true = schedule for later. */
  uberSchedule: boolean;
  setUberSchedule: (v: boolean) => void;
  /** ET wall-clock date (YYYY-MM-DD) for the scheduled Uber. */
  uberScheduleDate: string;
  setUberScheduleDate: (v: string) => void;
  /** ET wall-clock time (HH:MM) for the scheduled Uber. */
  uberScheduleTime: string;
  setUberScheduleTime: (v: string) => void;
}

export default function DropoffSection({
  dropDate,
  setDropDate,
  uberDropoff,
  setUberDropoff,
  uberReturn,
  setUberReturn,
  wantsStyling,
  setWantsStyling,
  dropoffWigCount,
  dropoffDaysByType,
  selectedDropoffServiceIds,
  uberSchedule,
  setUberSchedule,
  uberScheduleDate,
  setUberScheduleDate,
  uberScheduleTime,
  setUberScheduleTime,
}: DropoffSectionProps) {
  const { localDeliveryFee } = useSiteConfig();
  const t = useTranslations("booking");
  // Worst-case lead time across the customer's selected drop-off
  // services. If they picked color (3 days) + wash & set (1 day),
  // they need to plan for 3 days. Default 0 hides the callout.
  const leadDays = (() => {
    if (!dropoffDaysByType || !selectedDropoffServiceIds?.length) return 0;
    let max = 0;
    for (const id of selectedDropoffServiceIds) {
      const n = dropoffDaysByType[id] ?? 0;
      if (n > max) max = n;
    }
    return max;
  })();
  return (
    <section className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <Package className="h-4 w-4 text-brand-gold" />
        <h2 className="text-base font-semibold text-brand-dark">{t("dropoffHandleTitle")}</h2>
      </div>
      {leadDays > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
          <Clock className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <span>
            {t(dropoffWigCount > 1 ? "dropoffPlanAheadMany" : "dropoffPlanAheadOne", { days: leadDays })}
          </span>
        </div>
      )}
      <div className="space-y-2.5">
        {/* ── Option A — customer drops it off themselves ─────────────
            The drop-off date lives UNDER this option (it only applies
            when the customer is the one bringing it in). */}
        <div className="rounded-lg border border-brand-gold/30 bg-brand-cream/40 p-3 text-xs text-brand-dark">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="radio"
              name="dropoff-mode"
              checked={!uberDropoff}
              onChange={() => setUberDropoff(false)}
              className="mt-0.5 border-gray-300 text-brand-gold focus:ring-brand-gold"
            />
            <span className="flex-1">
              <span className="inline-flex items-center gap-1.5 font-medium">
                <Package className="h-3.5 w-3.5 text-brand-gold" />
                {t("uberSelfDropoff")}
              </span>
              <span className="block mt-1 text-[11px] text-brand-muted leading-relaxed">
                {t("uberSelfDropoffDesc")}
              </span>
            </span>
          </label>
          {!uberDropoff && (
            <div className="mt-2.5 ms-6 space-y-1.5">
              <p className="text-[11px] text-brand-muted">
                {t(dropoffWigCount > 1 ? "dropoffWhenMany" : "dropoffWhenOne")}
              </p>
              {/* Date picker — label-with-overlay-input pattern (bug
                  #125 — iOS invisible pill). The `for`/`id` link means
                  taps resolve to the input itself, so iOS Safari opens
                  the picker on first tap. */}
              <label
                htmlFor="dropoff-date-input"
                className="relative block w-full sm:w-64 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-brand-dark cursor-pointer hover:border-brand-gold focus-within:ring-2 focus-within:ring-brand-gold focus-within:border-transparent"
              >
                <span className={dropDate ? "text-brand-dark" : "text-gray-400"}>
                  {dropDate
                    ? new Date(dropDate + "T00:00:00").toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : t("dropoffChooseDate")}
                </span>
                <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gold/70 pointer-events-none" />
                <input
                  id="dropoff-date-input"
                  type="date"
                  value={dropDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setDropDate(e.target.value)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  aria-label={t("dropoffDateAria")}
                />
              </label>
            </div>
          )}
        </div>

        {/* ── Option B — send it in with Uber ─────────────────────────
            Now-vs-schedule nests under it (Shmuel 2026-07-03 — two
            top-level choices, timing is a sub-choice of Uber). We
            don't have the customer's ZIP at booking time, so the
            service-area disclosure is inline; admin reconfirms
            eligibility before dispatching. Fee reads from
            SiteConfig.localDeliveryFee (admin-editable). */}
        <div className="rounded-lg border border-brand-gold/30 bg-brand-cream/40 p-3 text-xs text-brand-dark">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="radio"
              name="dropoff-mode"
              checked={uberDropoff}
              onChange={() => setUberDropoff(true)}
              className="mt-0.5 border-gray-300 text-brand-gold focus:ring-brand-gold"
            />
            <span className="flex-1">
              <span className="inline-flex items-center gap-1.5 font-medium">
                <Truck className="h-3.5 w-3.5 text-brand-gold" />
                {t("uberSendInLabel")}
              </span>
              <span className="block mt-1 text-[11px] text-brand-muted leading-relaxed">
                {t("uberSendInDesc", { fee: formatPrice(localDeliveryFee) })}
              </span>
            </span>
          </label>
          {uberDropoff && (
            <div className="mt-2.5 ms-6 space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="uber-dispatch-timing"
                  checked={!uberSchedule}
                  onChange={() => setUberSchedule(false)}
                  className="mt-0.5 border-gray-300 text-brand-gold focus:ring-brand-gold"
                />
                <span className="font-medium">{t("uberScheduleNow")}</span>
              </label>
              {!uberSchedule && (
                <span className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] leading-relaxed text-amber-900">
                  <Clock className="h-3 w-3 flex-shrink-0 mt-0.5" />
                  <span>{t("uberSendInImmediate")}</span>
                </span>
              )}
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="uber-dispatch-timing"
                  checked={uberSchedule}
                  onChange={() => setUberSchedule(true)}
                  className="mt-0.5 border-gray-300 text-brand-gold focus:ring-brand-gold"
                />
                <span className="flex-1">
                  <span className="font-medium">{t("uberScheduleLater")}</span>
                  <span className="block mt-0.5 text-[11px] text-brand-muted leading-relaxed">
                    {t("uberScheduleLaterDesc")}
                  </span>
                </span>
              </label>
              {uberSchedule && (
                <div className="space-y-2">
                  <div className="flex flex-col sm:flex-row gap-2">
                    {/* Same label-overlay pattern as the drop-off date
                        picker (bug #125 — iOS invisible pill). */}
                    <label
                      htmlFor="uber-schedule-date-input"
                      className="relative block flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs text-brand-dark cursor-pointer hover:border-brand-gold focus-within:ring-2 focus-within:ring-brand-gold focus-within:border-transparent"
                    >
                      <span className={uberScheduleDate ? "text-brand-dark" : "text-gray-400"}>
                        {uberScheduleDate
                          ? new Date(uberScheduleDate + "T00:00:00").toLocaleDateString("en-US", {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                            })
                          : t("uberScheduleChooseDate")}
                      </span>
                      <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-gold/70 pointer-events-none" />
                      <input
                        id="uber-schedule-date-input"
                        type="date"
                        value={uberScheduleDate}
                        min={new Date().toISOString().slice(0, 10)}
                        max={new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)}
                        onChange={(e) => setUberScheduleDate(e.target.value)}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        aria-label={t("uberScheduleDateAria")}
                      />
                    </label>
                    <label
                      htmlFor="uber-schedule-time-input"
                      className="relative block flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs text-brand-dark cursor-pointer hover:border-brand-gold focus-within:ring-2 focus-within:ring-brand-gold focus-within:border-transparent"
                    >
                      <span className={uberScheduleTime ? "text-brand-dark" : "text-gray-400"}>
                        {uberScheduleTime ? formatTimeLabel(uberScheduleTime) : t("uberScheduleChooseTime")}
                      </span>
                      <Clock className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-gold/70 pointer-events-none" />
                      <input
                        id="uber-schedule-time-input"
                        type="time"
                        value={uberScheduleTime}
                        onChange={(e) => setUberScheduleTime(e.target.value)}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        aria-label={t("uberScheduleTimeAria")}
                      />
                    </label>
                  </div>
                  <p className="text-[11px] leading-relaxed text-brand-muted">
                    {t("uberScheduleNote")}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Leg 2 — return it with Uber when it's ready */}
        <label className="flex items-start gap-2.5 rounded-lg border border-brand-gold/30 bg-brand-cream/40 p-3 text-xs text-brand-dark cursor-pointer">
          <input
            type="checkbox"
            checked={uberReturn}
            onChange={(e) => setUberReturn(e.target.checked)}
            className="mt-0.5 rounded border-gray-300 text-brand-gold focus:ring-brand-gold"
          />
          <span className="flex-1">
            <span className="inline-flex items-center gap-1.5 font-medium">
              <Truck className="h-3.5 w-3.5 text-brand-gold" />
              {t("uberReturnLabel")}
            </span>
            <span className="block mt-1 text-[11px] text-brand-muted leading-relaxed">
              {leadDays > 0
                ? t("uberReturnDescDays", { fee: formatPrice(localDeliveryFee), days: leadDays })
                : t("uberReturnDescGeneric", { fee: formatPrice(localDeliveryFee) })}
            </span>
          </span>
        </label>

        {/* Optional — book a styling appointment for when it's ready */}
        <label className="flex items-start gap-2.5 rounded-lg border border-gray-200 bg-white p-3 text-xs text-brand-dark cursor-pointer">
          <input
            type="checkbox"
            checked={wantsStyling}
            onChange={(e) => setWantsStyling(e.target.checked)}
            className="mt-0.5 rounded border-gray-300 text-brand-gold focus:ring-brand-gold"
          />
          <span className="flex-1">
            <span className="inline-flex items-center gap-1.5 font-medium">
              <Calendar className="h-3.5 w-3.5 text-brand-gold" />
              {t("stylingLabel")}
            </span>
            <span className="block mt-1 text-[11px] text-brand-muted leading-relaxed">
              {t("stylingDesc")}
            </span>
          </span>
        </label>
      </div>
    </section>
  );
}
